import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { UsersThree, CheckCircle, Warning } from "@phosphor-icons/react";

const ROLE_LABEL = {
  owner: "Owner",
  editor: "Editor",
  viewer: "Viewer",
  executor: "Executor",
};

export default function InvitationAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshHouseholds, setActiveLedger } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get(`/invitations/${token}`)
      .then((r) => {
        if (active) setPreview(r.data);
      })
      .catch((err) => {
        if (active) setError(formatApiError(err.response?.data?.detail) || "Invitation not found.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const r = await api.post(`/invitations/${token}/accept`);
      toast.success(r.data.already_member ? "You were already a member." : "Welcome to the household.");
      await refreshHouseholds();
      setActiveLedger(r.data.household_id);
      navigate("/assets");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not accept invitation.");
    } finally {
      setAccepting(false);
    }
  };

  const goLogin = () => {
    navigate(`/login?next=${encodeURIComponent(`/invitations/${token}`)}`);
  };
  const goRegister = () => {
    navigate(`/register?next=${encodeURIComponent(`/invitations/${token}`)}`);
  };

  return (
    <div className="min-h-screen ledger-paper flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg" data-testid="invitation-accept-page">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img
            src="/logo-mark.png"
            alt="The Asset Arbor"
            className="w-12 h-12 object-contain shrink-0"
            draggable="false"
          />
          <div>
            <div className="font-serif-display text-2xl text-[#0A192F]">The Asset Arbor</div>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
              Household Invitation
            </div>
          </div>
        </div>

        <div className="border border-[#E0DDD6] bg-white p-8">
          {loading || authLoading ? (
            <div data-testid="invitation-loading" className="text-sm text-[#5C5F58] text-center py-8">
              Verifying invitation…
            </div>
          ) : error ? (
            <div data-testid="invitation-error" className="text-center py-4">
              <Warning size={32} weight="thin" className="mx-auto text-[#6B1718] mb-3" />
              <p className="font-serif-display text-xl text-[#0A192F]">{error}</p>
              <p className="text-xs text-[#5C5F58] mt-2">Ask the owner to issue a new invitation link.</p>
              <Link
                to="/"
                className="inline-block mt-6 text-xs text-[#1A3622] hover:underline font-mono-ledger uppercase tracking-[0.25em]"
              >
                Return home
              </Link>
            </div>
          ) : preview?.accepted ? (
            <div data-testid="invitation-already-accepted" className="text-center py-4">
              <CheckCircle size={32} weight="thin" className="mx-auto text-[#1A3622] mb-3" />
              <p className="font-serif-display text-xl text-[#0A192F]">
                This invitation has already been accepted.
              </p>
              <p className="text-xs text-[#5C5F58] mt-2">If you should have access, contact the household owner.</p>
              <Link
                to="/"
                className="inline-block mt-6 text-xs text-[#1A3622] hover:underline font-mono-ledger uppercase tracking-[0.25em]"
              >
                Return home
              </Link>
            </div>
          ) : (
            <div className="space-y-6" data-testid="invitation-details">
              <div className="text-center">
                <UsersThree size={32} weight="thin" className="mx-auto text-[#1A3622] mb-3" />
                <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                  You&apos;ve been invited to join
                </div>
                <h1 className="font-serif-display text-3xl text-[#0A192F] mt-2" data-testid="invitation-household-name">
                  {preview.household_name}
                </h1>
              </div>

              <div className="border-t border-b border-[#E0DDD6] py-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                    Invited by
                  </div>
                  <div className="text-[#0A192F]">{preview.invited_by_name || "—"}</div>
                </div>
                <div>
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                    Role
                  </div>
                  <div data-testid="invitation-role" className="text-[#0A192F]">
                    {ROLE_LABEL[preview.role] || preview.role}
                  </div>
                </div>
                {preview.email_hint && (
                  <div className="col-span-2">
                    <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                      Intended for
                    </div>
                    <div className="text-[#0A192F]">{preview.email_hint}</div>
                  </div>
                )}
              </div>

              {user ? (
                <div className="space-y-3">
                  <p className="text-xs text-[#5C5F58]">
                    Signed in as <strong className="text-[#0A192F]">{user.email}</strong>. Accept to join this
                    household ledger.
                  </p>
                  <button
                    onClick={accept}
                    disabled={accepting}
                    data-testid="invitation-accept-btn"
                    className="w-full bg-[#1A3622] text-[#F9F8F6] px-5 py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 transition-colors"
                  >
                    {accepting ? "Joining household…" : "Accept invitation"}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-[#5C5F58]">
                    Sign in or create an Asset Arbor account to accept this invitation.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={goLogin}
                      data-testid="invitation-login-btn"
                      className="bg-[#1A3622] text-[#F9F8F6] px-5 py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] transition-colors"
                    >
                      Sign in
                    </button>
                    <button
                      onClick={goRegister}
                      data-testid="invitation-register-btn"
                      className="border border-[#1A3622] text-[#1A3622] px-5 py-3 text-sm font-medium hover:bg-[#1A3622] hover:text-[#F9F8F6] transition-colors"
                    >
                      Create account
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
